import { useState, useRef, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Card, CardContent,
  Grid, CircularProgress, LinearProgress, Alert, Chip, Skeleton,
} from "@mui/material";
import { TrendingUp, Zap, BookOpen, History, Plus, Building2, Calendar } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectUser, checkAuth } from "../store/slices/authSlice";
import api from "../services/api";
import toast from "react-hot-toast";
import Navbar from "../components/dashboard/Navbar";
import UploadZone from "../components/dashboard/UploadZone";
import AnalysisResults from "../components/dashboard/AnalysisResults";
import QuestionBank from "../components/dashboard/QuestionBank";

const CATEGORY_COLORS = {
  Technical:     "#2563eb",
  Behavioral:    "#7c3aed",
  Situational:   "#d97706",
  "Culture Fit": "#059669",
};

function QBHistoryItem({ bank, onClick }) {
  const cats = bank.questions.reduce((acc, q) => {
    acc[q.category] = (acc[q.category] || 0) + 1;
    return acc;
  }, {});
  const date = bank.created_at?.toDate
    ? bank.created_at.toDate()
    : bank.created_at?._seconds
      ? new Date(bank.created_at._seconds * 1000)
      : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2, borderRadius: "12px", border: "1.5px solid #e2e8f0",
        cursor: "pointer", bgcolor: "#fff", transition: "all 0.18s",
        "&:hover": { borderColor: "#7c3aed", boxShadow: "0 4px 14px rgba(124,58,237,0.12)", transform: "translateY(-1px)" },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a", lineHeight: 1.4 }}>
          {bank.role || "Interview Questions"}
        </Typography>
        {date && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <Calendar size={11} color="#94a3b8" />
            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
              {date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </Typography>
          </Box>
        )}
      </Box>

      {bank.company_name && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Building2 size={11} color="#7c3aed" />
          <Typography sx={{ fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>{bank.company_name}</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        <Chip label={`${bank.questions.length} Q`} size="small"
          sx={{ bgcolor: "#f1f5f9", color: "#475569", fontSize: 10, fontWeight: 700, height: 20 }} />
        {Object.entries(cats).map(([cat, n]) => (
          <Chip key={cat} label={`${n} ${cat}`} size="small"
            sx={{
              bgcolor: `${CATEGORY_COLORS[cat] || "#64748b"}12`,
              color: CATEGORY_COLORS[cat] || "#64748b",
              fontSize: 10, fontWeight: 600, height: 20,
            }} />
        ))}
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // ── Mode ───────────────────────────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() =>
    searchParams.get("mode") === "questions" ? "questions" : "optimize"
  );
  useEffect(() => {
    if (searchParams.get("mode") === "questions") setMode("questions");
  }, [searchParams]);

  // ── QB sub-tab: "generate" | "history" ────────────────────────────────────
  const [qbSubTab, setQbSubTab] = useState("generate");

  // ── Shared inputs ──────────────────────────────────────────────────────────
  const [resume, setResume] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [companyName, setCompanyName] = useState("");
  const fileInputRef = useRef(null);

  // ── Optimize state ─────────────────────────────────────────────────────────
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [domainWarning, setDomainWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // ── Question bank state ────────────────────────────────────────────────────
  const [questionBank, setQuestionBank] = useState(null);
  const [qbError, setQbError] = useState(null);
  const [qbLoading, setQbLoading] = useState(false);

  // ── QB History state ───────────────────────────────────────────────────────
  const [qbHistory, setQbHistory] = useState([]);
  const [qbHistoryLoading, setQbHistoryLoading] = useState(false);
  const [qbHistoryLoaded, setQbHistoryLoaded] = useState(false);

  const loadQbHistory = useCallback(async () => {
    if (qbHistoryLoading) return;
    setQbHistoryLoading(true);
    try {
      const { data } = await api.get("/resume/question-bank/history");
      setQbHistory(data.banks || []);
      setQbHistoryLoaded(true);
    } catch {
      toast.error("Could not load history");
    } finally {
      setQbHistoryLoading(false);
    }
  }, [qbHistoryLoading]);

  // Auto-load history when switching to history tab
  useEffect(() => {
    if (qbSubTab === "history" && !qbHistoryLoaded) {
      loadQbHistory();
    }
  }, [qbSubTab, qbHistoryLoaded, loadQbHistory]);

  const toSlug = (str) =>
    (str || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 30);

  const isPaid = user?.plan_type === "paid" || analysis?.isPaidUser === true;

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10MB"); return; }
    const validTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ];
    if (!validTypes.includes(file.type)) { toast.error("Only PDF and DOCX files are supported"); return; }
    setResume(file);
    toast.success("Resume ready!");
  };

  // ── Analyze resume ─────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    if (!resume) { toast.error("Please upload your resume"); return; }
    if (!jobDescription.trim()) { toast.error("Please paste a job description"); return; }
    setLoading(true);
    setDomainWarning(null);
    setAnalysisError(null);
    try {
      const fd = new FormData();
      fd.append("resume", resume);
      fd.append("jobDescription", jobDescription);
      fd.append("companyName", companyName);
      const { data } = await api.post("/resume/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnalysis(data);
      toast.success(data.isCached ? "Loaded from cache" : "Analysis complete!");
      dispatch(checkAuth());
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.response?.data?.message || "Analysis failed. Please try again.";
      if (status === 422 && err.response?.data?.domainMismatch) {
        setDomainWarning(err.response.data.warning);
        setAnalysis(null);
      } else if (status === 403 || status === 402) {
        setAnalysis(null);
        setAnalysisError({ message: msg, type: "credits" });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Generate questions ─────────────────────────────────────────────────────
  const handleGenerateQuestions = async () => {
    if (!jobDescription.trim()) { toast.error("Please paste a job description"); return; }
    setQbLoading(true);
    setQbError(null);
    try {
      const { data } = await api.post("/resume/question-bank", {
        jobDescription,
        companyName,
      });
      setQuestionBank(data);
      // Prepend to cached history so the new item appears immediately
      setQbHistory((prev) => [data, ...prev]);
      setQbHistoryLoaded(true);
      toast.success("Question bank generated!");
      dispatch(checkAuth());
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.response?.data?.message || "Failed to generate questions. Please try again.";
      if (status === 403 || status === 402) {
        setQbError({ message: msg, type: "credits" });
      } else {
        toast.error(msg);
      }
    } finally {
      setQbLoading(false);
    }
  };

  // ── Download helpers ───────────────────────────────────────────────────────
  const triggerBlobDownload = (blobData, filename) => {
    const url = URL.createObjectURL(new Blob([blobData], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getBlobError = async (err) => {
    if (err.response?.status === 403) return null;
    try {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        return json.message || json.error || null;
      }
    } catch { /* ignore */ }
    return err.response?.data?.error || err.message || null;
  };

  const handleDownloadResume = async (selectedKeywords = [], template = 1, colorTheme = "navy") => {
    setDownloadLoading(true);
    try {
      const { data } = await api.post(
        "/resume/download-resume",
        { resumeData: analysis?.optimizedResume || null, selectedKeywords, template, colorTheme },
        { responseType: "blob" },
      );
      const candidateSlug = toSlug(analysis?.optimizedResume?.name);
      const companySlug = toSlug(companyName);
      const filename = [candidateSlug, companySlug].filter(Boolean).join("_") + "_resume.pdf" || "optimized_resume.pdf";
      triggerBlobDownload(data, filename);
      toast.success("Resume PDF downloaded!");
    } catch (err) {
      if (err.response?.status === 403) {
        navigate("/pricing");
      } else {
        const msg = await getBlobError(err);
        toast.error(msg || "Resume download failed. Please try again.");
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadCoverLetter = async ({ companyName: cn = "", hiringManagerName = "", selectedKeywords = [] } = {}) => {
    setDownloadLoading(true);
    try {
      const { data: gen } = await api.post("/resume/generate-cover-letter", {
        resumeData: analysis?.optimizedResume || null,
        jobDescription,
        companyName: cn,
        hiringManagerName,
        selectedKeywords,
      });
      const { data: dl } = await api.post(
        "/resume/download-cover-letter",
        {
          coverLetterText: gen.coverLetterText,
          companyName: cn,
          candidateName: analysis?.optimizedResume?.name || null,
          contactData: analysis?.optimizedResume?.contact || null,
        },
        { responseType: "blob" },
      );
      const candidateSlug = toSlug(analysis?.optimizedResume?.name);
      const companySlug = toSlug(cn);
      const filename = [candidateSlug, companySlug].filter(Boolean).join("_") + "_cover_letter.pdf" || "cover_letter.pdf";
      triggerBlobDownload(dl, filename);
      toast.success("Cover letter downloaded!");
    } catch (err) {
      if (err.response?.status === 403) {
        navigate("/pricing");
      } else {
        const msg = await getBlobError(err);
        toast.error(msg || "Cover letter generation failed. Please try again.");
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const isOptimize = mode === "optimize";
  const isBusy = loading || downloadLoading || qbLoading;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <Navbar />

      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>

        {/* ── Top mode toggle ── */}
        <Box sx={{ display: "inline-flex", bgcolor: "#f1f5f9", borderRadius: "14px", p: "4px", mb: 3, gap: "4px" }}>
          {[
            { key: "optimize",  label: "Optimize Resume", Icon: TrendingUp },
            { key: "questions", label: "Interview Prep",  Icon: BookOpen   },
          ].map(({ key, label, Icon }) => (
            <Button
              key={key}
              onClick={() => setMode(key)}
              startIcon={<Icon size={15} />}
              sx={{
                borderRadius: "10px",
                px: { xs: 1.5, md: 2.5 }, py: 1,
                fontSize: { xs: 12, md: 13 }, fontWeight: 700, textTransform: "none",
                minWidth: { xs: 130, md: 160 }, transition: "all 0.2s",
                ...(mode === key
                  ? { bgcolor: "#fff", color: key === "optimize" ? "#2563eb" : "#7c3aed", boxShadow: "0 1px 6px rgba(0,0,0,0.10)" }
                  : { bgcolor: "transparent", color: "#64748b", "&:hover": { bgcolor: "#e2e8f0" } }),
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        <Grid container spacing={3}>
          {/* ── LEFT: Input panel ── */}
          <Grid item xs={12} md={5}>

            {/* Resume upload — ONLY shown for Optimize mode */}
            {isOptimize && (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: "#0f172a" }}>
                    1. Upload Resume
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 2 }}>
                    PDF or DOCX · Max 10 MB
                  </Typography>
                  <UploadZone resume={resume} fileInputRef={fileInputRef} onUpload={handleFileUpload} disabled={isBusy} />
                </CardContent>
              </Card>
            )}

            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: "#0f172a" }}>
                  {isOptimize ? "2. Job Description" : "1. Job Description"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 2 }}>
                  {isOptimize
                    ? "Paste the full job listing to match keywords"
                    : "Paste the job listing — no resume needed"}
                </Typography>
                <TextField
                  fullWidth
                  label="Company Name"
                  placeholder="e.g. Google, Stripe, Apple"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  variant="outlined"
                  disabled={isBusy}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px", fontSize: 13,
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#93c5fd" },
                      "&.Mui-focused fieldset": { borderColor: isOptimize ? "#2563eb" : "#7c3aed" },
                    },
                  }}
                />
                <TextField
                  fullWidth multiline rows={isOptimize ? 8 : 10}
                  placeholder="Paste the job description here…"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value.slice(0, 4000))}
                  variant="outlined"
                  disabled={isBusy}
                  helperText={
                    <Box component="span" sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Box component="span" sx={{
                        fontSize: 11,
                        fontWeight: jobDescription.length >= 3800 ? 600 : 400,
                        color: jobDescription.length >= 4000 ? "#ef4444" : jobDescription.length >= 3800 ? "#f59e0b" : "#94a3b8",
                      }}>
                        {jobDescription.length} / 4000
                      </Box>
                    </Box>
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px", fontSize: 13,
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#93c5fd" },
                      "&.Mui-focused fieldset": { borderColor: isOptimize ? "#2563eb" : "#7c3aed" },
                    },
                    "& .MuiFormHelperText-root": { mx: 0 },
                  }}
                />
              </CardContent>
            </Card>

            {/* Action button */}
            {isOptimize ? (
              <Button
                fullWidth variant="contained" size="large"
                onClick={handleAnalyze}
                disabled={isBusy || !resume || !jobDescription.trim()}
                startIcon={!loading && <TrendingUp size={18} />}
                sx={{
                  py: 1.75, borderRadius: "10px", fontWeight: 700, fontSize: 15,
                  background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                  "&:hover": { background: "linear-gradient(135deg,#1d4ed8,#6d28d9)", boxShadow: "0 6px 20px rgba(37,99,235,0.4)" },
                  "&:disabled": { background: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {loading
                  ? <><CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />Analyzing…</>
                  : "Analyze Resume"}
              </Button>
            ) : (
              <Button
                fullWidth variant="contained" size="large"
                onClick={handleGenerateQuestions}
                disabled={isBusy || !jobDescription.trim()}
                startIcon={!qbLoading && <BookOpen size={18} />}
                sx={{
                  py: 1.75, borderRadius: "10px", fontWeight: 700, fontSize: 15,
                  background: "linear-gradient(135deg, #7c3aed, #2563eb)",
                  "&:hover": { background: "linear-gradient(135deg,#6d28d9,#1d4ed8)", boxShadow: "0 6px 20px rgba(124,58,237,0.4)" },
                  "&:disabled": { background: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {qbLoading
                  ? <><CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />Generating…</>
                  : "Generate Questions"}
              </Button>
            )}

            {(loading || qbLoading) && (
              <LinearProgress
                sx={{
                  mt: 1.5, borderRadius: 3, height: 4, bgcolor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": {
                    background: isOptimize
                      ? "linear-gradient(90deg,#2563eb,#7c3aed)"
                      : "linear-gradient(90deg,#7c3aed,#2563eb)",
                    borderRadius: 3,
                  },
                }}
              />
            )}

            {!isBusy && user?.credits !== -1 && (
              <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "#94a3b8", mt: 1.5 }}>
                {user?.credits ?? 0} {user?.credits === 1 ? "credit" : "credits"} remaining · 1 credit per action
              </Typography>
            )}
          </Grid>

          {/* ── RIGHT: Results panel ── */}
          <Grid item xs={12} md={7}>

            {/* ── Optimize mode ── */}
            {isOptimize && (
              <>
                {domainWarning && !loading && (
                  <Box sx={{ mb: 2.5, px: 2.5, py: 2, borderRadius: "12px", bgcolor: "#fffbeb", border: "1px solid #fde68a", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: 18, lineHeight: 1, mt: 0.1 }}>⚠️</Typography>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#92400e", mb: 0.25 }}>Domain Mismatch Detected</Typography>
                      <Typography variant="body2" sx={{ color: "#92400e", lineHeight: 1.5 }}>{domainWarning}</Typography>
                    </Box>
                  </Box>
                )}
                {analysisError && !loading && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2.5, borderRadius: "12px", alignItems: "flex-start" }}
                    action={
                      <Button size="small" variant="contained" startIcon={<Zap size={14} />}
                        onClick={() => navigate("/pricing")}
                        sx={{ borderRadius: "8px", fontWeight: 700, fontSize: 12, background: "linear-gradient(135deg,#2563eb,#7c3aed)", whiteSpace: "nowrap", mt: 0.25 }}>
                        Buy Credits
                      </Button>
                    }
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{analysisError.message}</Typography>
                  </Alert>
                )}
                <AnalysisResults
                  analysis={analysis} isPaid={isPaid} loading={loading}
                  onDownloadResume={handleDownloadResume} onDownloadCoverLetter={handleDownloadCoverLetter}
                  downloadLoading={downloadLoading} companyName={analysis?.company_name || companyName}
                />
              </>
            )}

            {/* ── Interview Prep mode ── */}
            {!isOptimize && (
              <Card>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>

                  {/* Sub-toggle: Generate | History */}
                  <Box sx={{
                    display: "flex", bgcolor: "#f8fafc", borderRadius: "10px",
                    p: "3px", mb: 2.5, gap: "3px",
                  }}>
                    {[
                      { key: "generate", label: "Generate New", Icon: Plus    },
                      { key: "history",  label: "My History",   Icon: History },
                    ].map(({ key, label, Icon }) => (
                      <Button
                        key={key}
                        fullWidth
                        onClick={() => setQbSubTab(key)}
                        startIcon={<Icon size={13} />}
                        sx={{
                          borderRadius: "8px", py: 0.75,
                          fontSize: 12, fontWeight: 700, textTransform: "none",
                          transition: "all 0.18s",
                          ...(qbSubTab === key
                            ? { bgcolor: "#fff", color: "#7c3aed", boxShadow: "0 1px 4px rgba(0,0,0,0.09)" }
                            : { bgcolor: "transparent", color: "#94a3b8", "&:hover": { bgcolor: "#f1f5f9" } }),
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>

                  {/* Credit error */}
                  {qbSubTab === "generate" && qbError && !qbLoading && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px", alignItems: "flex-start" }}
                      action={
                        <Button size="small" variant="contained" startIcon={<Zap size={14} />}
                          onClick={() => navigate("/pricing")}
                          sx={{ borderRadius: "8px", fontWeight: 700, fontSize: 12, background: "linear-gradient(135deg,#7c3aed,#2563eb)", whiteSpace: "nowrap", mt: 0.25 }}>
                          Buy Credits
                        </Button>
                      }
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{qbError.message}</Typography>
                    </Alert>
                  )}

                  {/* Generate tab */}
                  {qbSubTab === "generate" && (
                    <QuestionBank data={questionBank} loading={qbLoading} />
                  )}

                  {/* History tab */}
                  {qbSubTab === "history" && (
                    <Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                          Past Question Banks
                        </Typography>
                        <Button size="small" onClick={loadQbHistory} disabled={qbHistoryLoading}
                          sx={{ fontSize: 12, color: "#7c3aed", textTransform: "none", fontWeight: 600 }}>
                          Refresh
                        </Button>
                      </Box>

                      {qbHistoryLoading && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: "12px" }} />
                          ))}
                        </Box>
                      )}

                      {!qbHistoryLoading && qbHistory.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                          <History size={32} color="#cbd5e1" style={{ marginBottom: 8 }} />
                          <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No question banks yet.</Typography>
                          <Typography sx={{ color: "#cbd5e1", fontSize: 12, mt: 0.5 }}>
                            Generate your first one from the Generate tab.
                          </Typography>
                        </Box>
                      )}

                      {!qbHistoryLoading && qbHistory.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {qbHistory.map((bank, i) => (
                            <QBHistoryItem
                              key={bank.id ?? bank.docId ?? i}
                              bank={bank}
                              onClick={() => {
                                setQuestionBank(bank);
                                setQbSubTab("generate");
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                </CardContent>
              </Card>
            )}

          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
